import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Avatar} from '../../../../../../ui/atoms/avatar';
import {
  CancelIcon,
  MagnifyingGlassIcon,
} from '../../../../../../ui/atoms/icons';
import {ArrowHeadDownIcon} from '../../../../../../ui/atoms/icons/arrow-head-down';
import {activateNotification} from '../../../../../../ui/molecules/notification/activate-notification';
import {SelectField as BeamSelectField} from '../../../../../../ui/molecules/select/selectfield';
import {FlagIcon} from '../../../../../../ui/molecules/select/selectfield-secondary/flag-icon';
import {UserAPIController} from '../../../../../../api/user-api/user-api-controller';
import {UserAPIRequestImpl} from '../../../../../../api/user-api/user-api-request';
import {BaseURL} from '../../../../../../configs/request';
import {authStore} from '../../../../../../store/stores/auth-store';
import {
  getTimezoneOptions,
  normalizeTimezoneIdentifier,
  serializeTimezoneForApi,
} from '@utils/timezone';
import {groupController} from '../admin-group/admin-group-controller';
import {groupRequestImpl} from '../admin-group/admin-group-request';
import {reviewerId} from './member-role';
import {usePhoneCodes} from '../../../../../../utils/phone-codes';
import {
  CheckMark,
  CloseButton,
  CountryCode,
  CountryOption,
  CountryOptionName,
  CountryOptions,
  CountryPicker,
  CountryPopover,
  CountrySearch,
  CountrySearchIcon,
  CountryTrigger,
  DangerButton,
  Field,
  FieldError,
  FieldGrid,
  FieldLabelRow,
  GroupChoiceRow,
  GroupClearButton,
  GroupDropdown,
  GroupDropdownContent,
  GroupMorePill,
  GroupMoreTooltip,
  GroupMoreTooltipBubble,
  GroupOptionCopy,
  GroupOptionName,
  GroupOptions,
  GroupPickerButton,
  GroupTriggerPill,
  GroupTriggerPills,
  MemberIdentity,
  MemberIdentityText,
  ManagerChoiceRow,
  ManagerDropdown,
  ManagerDropdownCancel,
  ManagerDropdownContent,
  ManagerDropdownFooter,
  ManagerDropdownHeader,
  ManagerDropdownSearch,
  ManagerDropdownTitle,
  ManagerEmptyState,
  ManagerOptionBadge,
  ManagerOptionCopy,
  ManagerOptionMeta,
  ManagerOptionName,
  ManagerOptionNameLine,
  ManagerOptionsViewport,
  ManagerOptions,
  ManagerPickerAnchor,
  ManagerRemoveButton,
  ManagerListFade,
  ManagerTriggerName,
  ManagerTriggerValue,
  MemberMeta,
  MemberName,
  MemberNameLine,
  MemberPrimaryButton,
  MemberRoleBadge,
  Overlay,
  InlineFieldAction,
  InlineFieldActions,
  PickerButton,
  PhoneControl,
  PhoneNumberField,
  ProfileSelectCell,
  RoleDropdown,
  RoleOption,
  SecondaryButton,
  Sidecar,
  SidecarBody,
  SidecarCard,
  SidecarCardSubtitle,
  SidecarCardTitle,
  SidecarFooter,
  SidecarHeader,
  SidecarTitle,
  TextField,
} from './member-redesign.styles';

type MemberEditSidecarProps = {
  member: any;
  users: any[];
  groups: any[];
  groupTypes: any[];
  isManager: boolean;
  protectWorkspaceAccess?: boolean;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  onSuspend: (member: any) => void;
};

type Picker = 'manager' | 'groups' | 'role' | null;

const displayName = (user: any) =>
  user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.email || 'Unnamed member';

const normalizeIds = (ids: string[]) =>
  [...new Set(ids.filter(Boolean))].sort().join('|');

const groupTypeName = (group: any, groupTypes: any[]) => {
  if (group?.groupType?.name) return group.groupType.name;
  const groupTypeId = group?.groupType?.id || group?.groupType;
  return (
    groupTypes.find((groupType) => groupType.id === groupTypeId)?.name ||
    'Uncategorized'
  );
};

const CountryCodePicker = ({
  countries,
  value,
  onChange,
}: {
  countries: ReturnType<typeof usePhoneCodes>;
  value: string;
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedCountry = countries.find(
    (country) => country.dial_code === value,
  );
  const filteredCountries = countries.filter((country) =>
    `${country.name} ${country.dial_code}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  return (
    <CountryPicker ref={rootRef}>
      <CountryTrigger
        type="button"
        aria-label="Country code"
        aria-expanded={open}
        onClick={() => {
          setSearch('');
          setOpen((current) => !current);
        }}>
        <FlagIcon country={selectedCountry} />
        <CountryCode>{value || 'Select'}</CountryCode>
        <ArrowHeadDownIcon
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 160ms ease',
          }}
        />
      </CountryTrigger>
      {open && (
        <CountryPopover>
          <CountrySearch>
            <input
              autoFocus
              aria-label="Search countries"
              placeholder="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <CountrySearchIcon>
              <MagnifyingGlassIcon />
            </CountrySearchIcon>
          </CountrySearch>
          <CountryOptions>
            {filteredCountries.map((country) => (
              <CountryOption
                key={`${country.code}-${country.dial_code}`}
                type="button"
                $selected={
                  country.code === selectedCountry?.code &&
                  country.dial_code === selectedCountry?.dial_code
                }
                onClick={() => {
                  onChange(country.dial_code);
                  setOpen(false);
                }}>
                <FlagIcon country={country} />
                <CountryOptionName>{country.name}</CountryOptionName>
                <CountryCode>{country.dial_code}</CountryCode>
              </CountryOption>
            ))}
          </CountryOptions>
        </CountryPopover>
      )}
    </CountryPicker>
  );
};

export const MemberEditSidecar = ({
  member,
  users,
  groups,
  groupTypes,
  isManager,
  protectWorkspaceAccess = false,
  onClose,
  onSaved,
  onSuspend,
}: MemberEditSidecarProps) => {
  const initialForm = useMemo(
    () => ({
      firstName: member?.firstName || '',
      lastName: member?.lastName || '',
      phoneNumber: member?.phoneNumber || '',
      countryCode: member?.countryCode || '',
      jobTitle: member?.jobTitle || '',
      timezone: normalizeTimezoneIdentifier(member?.timezone),
      role: member?.role === 'admin' ? 'admin' : 'user',
    }),
    [member],
  );
  const initialGroupIds = useMemo(
    () => (member?.group || []).map((item: any) => item?.id || item?.group),
    [member],
  );
  const initialManagerId = reviewerId(member?.reviewer);
  const [form, setForm] = useState(initialForm);
  const [groupIds, setGroupIds] = useState<string[]>(initialGroupIds);
  const [managerId, setManagerId] = useState(initialManagerId);
  const [picker, setPicker] = useState<Picker>(null);
  const [search, setSearch] = useState('');
  const [draftGroupIds, setDraftGroupIds] = useState<string[]>(initialGroupIds);
  const [draftManagerId, setDraftManagerId] = useState(managerId);
  const [saving, setSaving] = useState(false);
  const [savedEmail, setSavedEmail] = useState(member?.email || '');
  const [email, setEmail] = useState(member?.email || '');
  const [editingEmail, setEditingEmail] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [managerScrollIndicators, setManagerScrollIndicators] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });
  const [groupScrollIndicators, setGroupScrollIndicators] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });
  const managerPickerRef = useRef<HTMLDivElement>(null);
  const groupPickerRef = useRef<HTMLDivElement>(null);
  const rolePickerRef = useRef<HTMLDivElement>(null);
  const managerDropdownRef = useRef<HTMLDivElement>(null);
  const groupDropdownRef = useRef<HTMLDivElement>(null);
  const managerOptionsRef = useRef<HTMLDivElement>(null);
  const groupOptionsRef = useRef<HTMLDivElement>(null);
  const sidecarBodyRef = useRef<HTMLDivElement>(null);
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const phoneCodes = usePhoneCodes();
  const sortedPhoneCodes = useMemo(
    () => [...phoneCodes].sort((a, b) => a.name.localeCompare(b.name)),
    [phoneCodes],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (picker) setPicker(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, picker]);

  useEffect(() => {
    if (picker !== 'manager' && picker !== 'groups' && picker !== 'role')
      return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      const activePicker =
        picker === 'manager'
          ? managerPickerRef.current
          : picker === 'groups'
          ? groupPickerRef.current
          : rolePickerRef.current;
      if (!activePicker?.contains(event.target as Node)) {
        setPicker(null);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [picker]);

  useEffect(() => {
    if (picker !== 'manager' && picker !== 'groups') return;

    const scrollFrame = window.requestAnimationFrame(() => {
      const dropdown =
        picker === 'manager'
          ? managerDropdownRef.current
          : groupDropdownRef.current;
      dropdown?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });

    return () => window.cancelAnimationFrame(scrollFrame);
  }, [picker]);

  const selectedGroups = groups.filter((group) => groupIds.includes(group.id));
  const selectedManager = users.find((user) => user.id === managerId);
  const managerOptions = users.filter(
    (user) =>
      user.id !== member.id &&
      displayName(user).toLowerCase().includes(search.toLowerCase()),
  );
  const filteredGroups = groups.filter((group) =>
    group.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const updateManagerScrollIndicators = useCallback(() => {
    const options = managerOptionsRef.current;
    if (!options) {
      setManagerScrollIndicators({canScrollUp: false, canScrollDown: false});
      return;
    }

    setManagerScrollIndicators({
      canScrollUp: options.scrollTop > 2,
      canScrollDown:
        options.scrollTop + options.clientHeight < options.scrollHeight - 2,
    });
  }, []);

  const updateGroupScrollIndicators = useCallback(() => {
    const options = groupOptionsRef.current;
    if (!options) {
      setGroupScrollIndicators({canScrollUp: false, canScrollDown: false});
      return;
    }

    setGroupScrollIndicators({
      canScrollUp: options.scrollTop > 2,
      canScrollDown:
        options.scrollTop + options.clientHeight < options.scrollHeight - 2,
    });
  }, []);

  useEffect(() => {
    if (picker !== 'manager') {
      setManagerScrollIndicators({canScrollUp: false, canScrollDown: false});
      return;
    }

    managerOptionsRef.current?.scrollTo({top: 0});
    const measureFrame = window.requestAnimationFrame(
      updateManagerScrollIndicators,
    );
    return () => window.cancelAnimationFrame(measureFrame);
  }, [managerOptions.length, picker, search, updateManagerScrollIndicators]);

  useEffect(() => {
    const options = managerOptionsRef.current;
    if (picker !== 'manager' || !options) return;

    const observer = new ResizeObserver(updateManagerScrollIndicators);
    observer.observe(options);
    return () => observer.disconnect();
  }, [picker, updateManagerScrollIndicators]);

  useEffect(() => {
    if (picker !== 'groups') {
      setGroupScrollIndicators({canScrollUp: false, canScrollDown: false});
      return;
    }

    groupOptionsRef.current?.scrollTo({top: 0});
    const measureFrame = window.requestAnimationFrame(
      updateGroupScrollIndicators,
    );
    return () => window.cancelAnimationFrame(measureFrame);
  }, [filteredGroups.length, picker, search, updateGroupScrollIndicators]);

  useEffect(() => {
    const options = groupOptionsRef.current;
    if (picker !== 'groups' || !options) return;

    const observer = new ResizeObserver(updateGroupScrollIndicators);
    observer.observe(options);
    return () => observer.disconnect();
  }, [picker, updateGroupScrollIndicators]);
  const hasChanges = useMemo(
    () =>
      Object.keys(initialForm).some(
        (key) =>
          form[key as keyof typeof form] !==
          initialForm[key as keyof typeof initialForm],
      ) ||
      normalizeIds(groupIds) !== normalizeIds(initialGroupIds) ||
      managerId !== initialManagerId,
    [form, groupIds, initialForm, initialGroupIds, initialManagerId, managerId],
  );

  const updateField = (key: string, value: string) =>
    setForm((current) => ({...current, [key]: value}));

  const cancelEmailChange = () => {
    setEmail(savedEmail);
    setEmailError('');
    setEditingEmail(false);
  };

  const saveEmail = async () => {
    const nextEmail = email.trim();
    if (!nextEmail) {
      setEmailError('Fill in the member email');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setEmailError('Enter a valid email address');
      return;
    }
    if (nextEmail === savedEmail || savingEmail) return;

    setSavingEmail(true);
    const userController = new UserAPIController(new UserAPIRequestImpl());
    const updated = await userController.updateUser(
      {email: nextEmail},
      member.id,
      true,
    );

    if (updated) {
      setSavedEmail(nextEmail);
      setEmail(nextEmail);
      setEditingEmail(false);
      setEmailError('');
      activateNotification({
        title: 'Success',
        content: `${displayName(member)}'s email was updated`,
        kind: 'success',
      });
      await onSaved();
    }
    setSavingEmail(false);
  };

  const openPicker = (next: Picker) => {
    setSearch('');
    setDraftGroupIds(groupIds);
    setDraftManagerId(managerId);
    setPicker(next);
  };

  const save = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    const userController = new UserAPIController(new UserAPIRequestImpl());
    const groupsController = new groupController(new groupRequestImpl());
    const updated = await userController.updateUser(
      {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        countryCode: form.countryCode.trim(),
        jobTitle: form.jobTitle.trim(),
        timezone: serializeTimezoneForApi(form.timezone),
        role: form.role,
        ...(managerId
          ? {reviewer: managerId}
          : reviewerId(member.reviewer)
          ? {removeReviewer: true}
          : {}),
      },
      member.id,
      true,
    );

    if (!updated) {
      setSaving(false);
      return;
    }

    const additions = groupIds.filter((id) => !initialGroupIds.includes(id));
    const removals = initialGroupIds.filter(
      (id: string) => !groupIds.includes(id),
    );
    await Promise.all([
      ...additions.map((id) =>
        groupsController.newAddGroupMember(
          {add: {users: [member.id], type: 'add'}},
          id,
        ),
      ),
      ...removals.map((id: string) =>
        groupsController.newRemoveGroupMember(
          {remove: {users: [member.id]}},
          id,
        ),
      ),
    ]);

    activateNotification({
      title: 'Success',
      content: `${displayName(member)}'s profile was updated`,
      kind: 'success',
    });
    await onSaved();
    setSaving(false);
    onClose();
  };

  const resetPassword = async () => {
    const response = await fetch(`${BaseURL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authStore.auth?.tokens.access.token}`,
      },
      body: JSON.stringify({email: member.email}),
    });
    activateNotification({
      title: response.ok ? 'Password reset sent' : 'Error',
      content: response.ok
        ? `A password reset was sent to ${member.email}`
        : 'The password reset could not be sent.',
      kind: response.ok ? 'success' : 'error',
    });
  };

  return (
    <>
      <Overlay onClick={onClose} />
      <Sidecar role="dialog" aria-modal="true" aria-label="Edit profile">
        <SidecarHeader>
          <SidecarTitle>Edit profile</SidecarTitle>
          <CloseButton type="button" onClick={onClose}>
            <CancelIcon />
            Close
          </CloseButton>
        </SidecarHeader>

        <SidecarBody ref={sidecarBodyRef}>
          <SidecarCard>
            <MemberIdentity>
              <Avatar
                name={displayName(member)}
                userId={member.id}
                src={member.avatar?.url || ''}
                size="lg"
                tooltip={false}
              />
              <MemberIdentityText>
                <MemberNameLine>
                  <MemberName>{displayName(member)}</MemberName>
                  <MemberRoleBadge>
                    {form.role === 'admin' ? 'Admin access' : 'Standard access'}
                  </MemberRoleBadge>
                  {isManager && <MemberRoleBadge>Manager</MemberRoleBadge>}
                </MemberNameLine>
                <MemberMeta>{savedEmail}</MemberMeta>
              </MemberIdentityText>
            </MemberIdentity>
          </SidecarCard>

          <SidecarCard>
            <SidecarCardTitle>Personal details</SidecarCardTitle>
            <SidecarCardSubtitle>
              Update the member's basic profile information.
            </SidecarCardSubtitle>
            <FieldGrid>
              <Field>
                First name
                <TextField
                  value={form.firstName}
                  onChange={(event) =>
                    updateField('firstName', event.target.value)
                  }
                />
              </Field>
              <Field>
                Last name
                <TextField
                  value={form.lastName}
                  onChange={(event) =>
                    updateField('lastName', event.target.value)
                  }
                />
              </Field>
              <Field as="div">
                Phone Number
                <PhoneControl>
                  <CountryCodePicker
                    countries={sortedPhoneCodes}
                    value={form.countryCode}
                    onChange={(value) => updateField('countryCode', value)}
                  />
                  <PhoneNumberField
                    aria-label="Phone number"
                    value={form.phoneNumber}
                    onChange={(event) =>
                      updateField('phoneNumber', event.target.value)
                    }
                  />
                </PhoneControl>
              </Field>
              <Field as="div">
                <FieldLabelRow>
                  <span>Email address</span>
                  {!editingEmail ? (
                    <InlineFieldAction
                      type="button"
                      onClick={() => {
                        setEmailError('');
                        setEditingEmail(true);
                      }}>
                      Change Email
                    </InlineFieldAction>
                  ) : (
                    <InlineFieldActions>
                      <InlineFieldAction
                        type="button"
                        $danger
                        onClick={cancelEmailChange}>
                        Cancel
                      </InlineFieldAction>
                      <InlineFieldAction
                        type="button"
                        disabled={
                          savingEmail || email.trim() === savedEmail.trim()
                        }
                        onClick={saveEmail}>
                        {savingEmail ? 'Saving…' : 'Save'}
                      </InlineFieldAction>
                    </InlineFieldActions>
                  )}
                </FieldLabelRow>
                <TextField
                  value={email}
                  disabled={!editingEmail || savingEmail}
                  type="email"
                  autoComplete="email"
                  aria-invalid={Boolean(emailError)}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      saveEmail();
                    }
                  }}
                />
                {emailError && (
                  <FieldError role="alert">{emailError}</FieldError>
                )}
              </Field>
              <Field>
                Job title
                <TextField
                  value={form.jobTitle}
                  onChange={(event) =>
                    updateField('jobTitle', event.target.value)
                  }
                />
              </Field>
              <ProfileSelectCell>
                <BeamSelectField
                  label="Time Zone"
                  placeholder="Select timezone"
                  reportingStatus
                  borderRadius={8}
                  classNamePrefix="select"
                  margin
                  options={timezoneOptions}
                  value={form.timezone}
                  defaultValue={form.timezone}
                  onChange={(data: any) =>
                    updateField('timezone', data?.value || '')
                  }
                />
              </ProfileSelectCell>
            </FieldGrid>
          </SidecarCard>

          <SidecarCard>
            <SidecarCardTitle>Workspace</SidecarCardTitle>
            <SidecarCardSubtitle>
              Manage groups, reporting line, and workspace access.
            </SidecarCardSubtitle>
            <Field as="div">
              Groups
              <ManagerPickerAnchor
                ref={groupPickerRef}
                $open={picker === 'groups'}>
                <GroupPickerButton
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={picker === 'groups'}
                  onClick={() =>
                    picker === 'groups' ? setPicker(null) : openPicker('groups')
                  }>
                  <GroupTriggerPills>
                    {selectedGroups.length ? (
                      <>
                        {selectedGroups.slice(0, 3).map((group) => (
                          <GroupTriggerPill key={group.id} title={group.name}>
                            {group.name}
                          </GroupTriggerPill>
                        ))}
                        {selectedGroups.length > 3 && (
                          <GroupMoreTooltip>
                            <GroupMorePill>
                              +{selectedGroups.length - 3}
                            </GroupMorePill>
                            <GroupMoreTooltipBubble role="tooltip">
                              {selectedGroups
                                .slice(3)
                                .map((group) => group.name)
                                .join(', ')}
                            </GroupMoreTooltipBubble>
                          </GroupMoreTooltip>
                        )}
                      </>
                    ) : (
                      <ManagerTriggerName>
                        No groups selected
                      </ManagerTriggerName>
                    )}
                  </GroupTriggerPills>
                  <ArrowHeadDownIcon
                    style={{
                      transform:
                        picker === 'groups' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                </GroupPickerButton>

                {picker === 'groups' && (
                  <GroupDropdown
                    ref={groupDropdownRef}
                    role="dialog"
                    aria-label="Update groups"
                    onClick={(event) => event.stopPropagation()}>
                    <ManagerDropdownHeader>
                      <ManagerDropdownTitle>Update groups</ManagerDropdownTitle>
                      <ManagerDropdownCancel
                        type="button"
                        onClick={() => setPicker(null)}>
                        Cancel
                      </ManagerDropdownCancel>
                    </ManagerDropdownHeader>
                    <GroupDropdownContent>
                      <ManagerDropdownSearch>
                        <input
                          autoFocus
                          aria-label="Search groups"
                          placeholder="Search groups"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                        />
                        <MagnifyingGlassIcon />
                      </ManagerDropdownSearch>
                      <ManagerOptionsViewport>
                        {groupScrollIndicators.canScrollUp && (
                          <ManagerListFade position="top" aria-hidden="true" />
                        )}
                        <GroupOptions
                          ref={groupOptionsRef}
                          role="listbox"
                          aria-multiselectable="true"
                          onScroll={updateGroupScrollIndicators}>
                          {filteredGroups.map((group) => {
                            const selected = draftGroupIds.includes(group.id);
                            return (
                              <GroupChoiceRow
                                key={group.id}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                $selected={selected}
                                onClick={() =>
                                  setDraftGroupIds((current) =>
                                    selected
                                      ? current.filter((id) => id !== group.id)
                                      : [...current, group.id],
                                  )
                                }>
                                <CheckMark $selected={selected}>
                                  {selected ? '✓' : ''}
                                </CheckMark>
                                <GroupOptionCopy>
                                  <GroupOptionName $selected={selected}>
                                    {group.name}
                                  </GroupOptionName>
                                  <ManagerOptionBadge>
                                    {groupTypeName(group, groupTypes)}
                                  </ManagerOptionBadge>
                                </GroupOptionCopy>
                              </GroupChoiceRow>
                            );
                          })}
                          {!filteredGroups.length && (
                            <ManagerEmptyState>
                              No groups match your search.
                            </ManagerEmptyState>
                          )}
                        </GroupOptions>
                        {groupScrollIndicators.canScrollDown && (
                          <ManagerListFade
                            position="bottom"
                            aria-hidden="true"
                          />
                        )}
                      </ManagerOptionsViewport>
                    </GroupDropdownContent>
                    <ManagerDropdownFooter>
                      <GroupClearButton
                        type="button"
                        disabled={!draftGroupIds.length}
                        onClick={() => setDraftGroupIds([])}>
                        Clear groups
                      </GroupClearButton>
                      <MemberPrimaryButton
                        type="button"
                        onClick={() => {
                          setGroupIds(draftGroupIds);
                          setPicker(null);
                        }}>
                        Apply
                      </MemberPrimaryButton>
                    </ManagerDropdownFooter>
                  </GroupDropdown>
                )}
              </ManagerPickerAnchor>
            </Field>
            <Field as="div" style={{marginTop: 16}}>
              Manager
              <ManagerPickerAnchor
                ref={managerPickerRef}
                $open={picker === 'manager'}>
                <PickerButton
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={picker === 'manager'}
                  onClick={() =>
                    picker === 'manager'
                      ? setPicker(null)
                      : openPicker('manager')
                  }>
                  <ManagerTriggerValue>
                    {selectedManager && (
                      <Avatar
                        name={displayName(selectedManager)}
                        userId={selectedManager.id}
                        src={selectedManager.avatar?.url || ''}
                        size="md"
                        tooltip={false}
                      />
                    )}
                    <ManagerTriggerName>
                      {selectedManager
                        ? displayName(selectedManager)
                        : 'No manager'}
                    </ManagerTriggerName>
                  </ManagerTriggerValue>
                  <ArrowHeadDownIcon
                    style={{
                      transform:
                        picker === 'manager'
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                </PickerButton>

                {picker === 'manager' && (
                  <ManagerDropdown
                    ref={managerDropdownRef}
                    role="dialog"
                    aria-label="Change manager"
                    onClick={(event) => event.stopPropagation()}>
                    <ManagerDropdownHeader>
                      <ManagerDropdownTitle>
                        Change manager
                      </ManagerDropdownTitle>
                      <ManagerDropdownCancel
                        type="button"
                        onClick={() => setPicker(null)}>
                        Cancel
                      </ManagerDropdownCancel>
                    </ManagerDropdownHeader>
                    <ManagerDropdownContent>
                      <ManagerDropdownSearch>
                        <input
                          autoFocus
                          aria-label="Search managers"
                          placeholder="Search managers"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                        />
                        <MagnifyingGlassIcon />
                      </ManagerDropdownSearch>
                      <ManagerOptionsViewport>
                        {managerScrollIndicators.canScrollUp && (
                          <ManagerListFade position="top" aria-hidden="true" />
                        )}
                        <ManagerOptions
                          ref={managerOptionsRef}
                          role="listbox"
                          onScroll={updateManagerScrollIndicators}>
                          {managerOptions.map((user) => {
                            const selected = draftManagerId === user.id;
                            return (
                              <ManagerChoiceRow
                                key={user.id}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                $selected={selected}
                                onClick={() => setDraftManagerId(user.id)}>
                                <Avatar
                                  name={displayName(user)}
                                  userId={user.id}
                                  src={user.avatar?.url || ''}
                                  size="md"
                                  tooltip={false}
                                />
                                <ManagerOptionCopy>
                                  <ManagerOptionNameLine>
                                    <ManagerOptionName $selected={selected}>
                                      {displayName(user)}
                                    </ManagerOptionName>
                                    <ManagerOptionBadge>
                                      Manager
                                    </ManagerOptionBadge>
                                  </ManagerOptionNameLine>
                                  <ManagerOptionMeta>
                                    {user.jobTitle || user.email}
                                  </ManagerOptionMeta>
                                </ManagerOptionCopy>
                              </ManagerChoiceRow>
                            );
                          })}
                          {!managerOptions.length && (
                            <ManagerEmptyState>
                              No managers match your search.
                            </ManagerEmptyState>
                          )}
                        </ManagerOptions>
                        {managerScrollIndicators.canScrollDown && (
                          <ManagerListFade
                            position="bottom"
                            aria-hidden="true"
                          />
                        )}
                      </ManagerOptionsViewport>
                    </ManagerDropdownContent>
                    <ManagerDropdownFooter>
                      <ManagerRemoveButton
                        type="button"
                        disabled={!managerId}
                        onClick={() => {
                          setManagerId('');
                          setPicker(null);
                        }}>
                        Remove Manager
                      </ManagerRemoveButton>
                      <MemberPrimaryButton
                        type="button"
                        disabled={!draftManagerId}
                        onClick={() => {
                          setManagerId(draftManagerId);
                          setPicker(null);
                        }}>
                        Apply
                      </MemberPrimaryButton>
                    </ManagerDropdownFooter>
                  </ManagerDropdown>
                )}
              </ManagerPickerAnchor>
            </Field>
            <Field style={{marginTop: 16}}>
              Workspace access
              <ManagerPickerAnchor
                ref={rolePickerRef}
                $open={picker === 'role'}>
                <PickerButton
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={picker === 'role'}
                  disabled={protectWorkspaceAccess}
                  title={
                    protectWorkspaceAccess
                      ? 'Assign another workspace admin before changing this role.'
                      : undefined
                  }
                  onClick={() =>
                    picker === 'role' ? setPicker(null) : openPicker('role')
                  }>
                  <ManagerTriggerName>
                    {form.role === 'admin' ? 'Admin access' : 'Standard access'}
                  </ManagerTriggerName>
                  <ArrowHeadDownIcon
                    style={{
                      transform:
                        picker === 'role' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 160ms ease',
                    }}
                  />
                </PickerButton>
                {picker === 'role' && (
                  <RoleDropdown role="listbox" aria-label="Select role">
                    {[
                      {value: 'user', label: 'Standard access'},
                      {value: 'admin', label: 'Admin access'},
                    ].map((option) => {
                      const selected = form.role === option.value;
                      return (
                        <RoleOption
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          $selected={selected}
                          onClick={() => {
                            updateField('role', option.value);
                            setPicker(null);
                          }}>
                          {option.label}
                        </RoleOption>
                      );
                    })}
                  </RoleDropdown>
                )}
              </ManagerPickerAnchor>
            </Field>
            {protectWorkspaceAccess && (
              <MemberMeta style={{marginTop: 8}}>
                This is the only active workspace admin. Assign another admin
                before changing this role.
              </MemberMeta>
            )}
          </SidecarCard>

          <SidecarCard>
            <SidecarCardTitle>Others</SidecarCardTitle>
            <SidecarCardSubtitle>
              Send a password reset or suspend this member's access.
            </SidecarCardSubtitle>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
              <SecondaryButton type="button" onClick={resetPassword}>
                Reset password
              </SecondaryButton>
              <DangerButton
                type="button"
                disabled={protectWorkspaceAccess}
                title={
                  protectWorkspaceAccess
                    ? 'Assign another workspace admin before suspending this member.'
                    : undefined
                }
                onClick={() => onSuspend(member)}>
                Suspend member
              </DangerButton>
            </div>
            {protectWorkspaceAccess && (
              <MemberMeta style={{marginTop: 10}}>
                This is the only active workspace admin. Assign another admin
                before suspending this account.
              </MemberMeta>
            )}
          </SidecarCard>
        </SidecarBody>
        <SidecarFooter>
          <MemberPrimaryButton
            type="button"
            disabled={!hasChanges || saving}
            onClick={save}>
            Save changes
          </MemberPrimaryButton>
        </SidecarFooter>
      </Sidecar>
    </>
  );
};
